import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Autocomplete,Button,Card,CardContent,InputAdornment,Pagination,Stack,TextField,Tooltip,Typography } from "@mui/material";
import { useEffect,useState } from "react";
import { useTranslation } from "react-i18next";
import { FaRegSquareCheck } from "react-icons/fa6";
import { trackEventMatomo } from "../../helpers/matomo";
import { planoDeAcaoType } from "../../types/plano-de-acao/types";
import ModalAvalia from "./modal/ModalAvalia";

export default function Avaliacao({ planos, getAvaliacao }: { planos: planoDeAcaoType[], getAvaliacao: Function }) {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState<number>(5);
  const [paginated, setPaginated] = useState<planoDeAcaoType[]>([]);
  const [selectedPlano, setSelectedPlano] = useState<planoDeAcaoType>();
  const { t } = useTranslation('translation');


  const lastPageIndex = currentPage * itemsPerPage;
  const firstPageIndex = lastPageIndex - itemsPerPage;

  const pages = [];

  for (let i = 1; i <= Math.ceil(planos?.filter((elem) => elem.nome?.includes(search))?.length / itemsPerPage); i++) {
    pages.push(i);
  }

  useEffect(() => {
    if (planos) {
      const currentPageData = planos.filter((ele) => ele.nome?.includes(search))
        ?.sort((a, b) => { return Number(a.dataCriacao) < Number(b.dataCriacao) ? 1 : -1; });
      setPaginated(currentPageData?.slice(firstPageIndex, lastPageIndex) ?? []);
    }
  }, [firstPageIndex, lastPageIndex, planos, t, search]);

  return (
    <>
      {openModal && <ModalAvalia
        openModal={openModal}
        setOpenModal={setOpenModal}
        plano={selectedPlano as planoDeAcaoType}
        setSelectedPlano={setSelectedPlano}
        tab={'pendentes'}
      />}
      <Stack
        direction='column'
        width='100%'
        justifyContent='flex-start'
        alignItems='flex-start'
        spacing={2}
      >
        {planos.length
          ? <>
            {pages?.length > 1
              && <Pagination
                style={{ display: `${!pages?.length ? 'none' : ''}`, marginTop: '5px' }}
                boundaryCount={4}
                showFirstButton
                showLastButton
                shape="rounded"
                count={pages?.length}
                onChange={(eve, pageNumber) => {
                  trackEventMatomo('historico PA', 'click', 'button', 'muda página');
                  setCurrentPage(pageNumber);
                }}
              />}
            {planos.length > 5
              && <Autocomplete
                fullWidth
                value={search ? ({ nome: search } as planoDeAcaoType) : null}
                getOptionLabel={option => option.nome as string}
                getOptionKey={option => option?.id as string}
                isOptionEqualToValue={(option, value) => option.nome === value.nome}
                options={planos
                  ?.sort((a, b) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : -1; })
                  ?.map((ele: planoDeAcaoType) => ({ ...ele })) || []}
                onChange={(_eve, value: planoDeAcaoType | null) => {
                  if (value?.nome) {
                    setSearch(value?.nome as string);
                    setCurrentPage(1);
                  }
                  else {
                    setSearch("");
                  }
                }}
                renderInput={(params) => <TextField {...params} label={t('ActionPlan.PlanosDeAcaoComp.searchPlanByName')} />}
              />}
            {paginated?.map((plano: planoDeAcaoType, index: number) => (
              <Card key={index} sx={{ padding: 2, width: '100%' }}>
                <CardContent>
                  <Stack direction='row' justifyContent='space-between' width='100%' alignItems='flex-start'>
                    <Stack direction='column' justifyContent='flex-start' alignItems='flex-start' spacing={1} width='100%'>
                      <Tooltip title={plano.nome}>
                        <Typography variant='h5' fontWeight={600} sx={{ color: '#47AA9C' }}>
                          {plano.nome.length > 15 ? plano.nome.slice(0, 15).concat('...') : plano.nome}
                        </Typography>
                      </Tooltip>
                      <Typography variant="body2" color='textDisabled'>
                        {t('ActionPlan.Historico.createdBy')} <b>{plano.criadorNome ? plano.criadorNome : '-'}</b> {t('ActionPlan.Historico.on')} {new Date(Number(plano?.dataCriacao))
                          ?.toLocaleString('pt-BR')} {t('ActionPlan.Historico.fromChecklist')} <b>{plano.formularioNome}</b>
                      </Typography>
                      <Typography variant="body2" color='textDisabled'>
                        {t('ActionPlan.Historico.responsible')} <b>{plano?.responsavelNome}</b>
                      </Typography>
                    </Stack>
                    <Stack direction='column' width='100%' alignItems='flex-end' justifyContent='flex-end' spacing={1.5}>
                      <Stack direction='row' width='100%' justifyContent='flex-end' alignItems='center' spacing={1}>
                        <Typography color='textDisabled' fontWeight={600} variant="h6">{t('ActionPlan.Historico.evaluation')}</Typography>
                        {getAvaliacao(String(plano?.avaliacao))}
                      </Stack>
                      <TextField
                        size="small"
                        inputProps={{
                          style: { textAlign: 'center' },
                        }}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarMonthIcon />
                              </InputAdornment>
                            ),
                          },
                        }}
                        disabled
                        label={t('ActionPlan.Historico.planClosingDate')}
                        value={new Date(Number(plano?.dataFechamento))?.toLocaleString('pt-BR')} />
                      <TextField
                        size="small"
                        inputProps={{
                          style: { textAlign: 'center' },
                        }}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarMonthIcon />
                              </InputAdornment>
                            ),
                          },
                        }}
                        disabled
                        label={t('ActionPlan.Historico.analysisDate')}
                        value={new Date(Number(plano?.dataAvaliacao))?.toLocaleString('pt-BR')} />
                      <Button
                        variant='contained'
                        color="info"
                        endIcon={<FaRegSquareCheck />}
                        onClick={() => {
                          trackEventMatomo('historico PA', 'click', 'button', 'fecha modal');
                          setSelectedPlano(plano);
                          setOpenModal(!openModal);
                        }}
                      >
                        {t('ActionPlan.Historico.evaluateEffectiveness')}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </> : <Typography variant="h5" color='textDisabled'>
            Nenhum plano avaliado
          </Typography>}
      </Stack>
    </>
  );
}
